/** @file commitRig: a typed size or anchor: drawn at once (quadOverride), then landed like a handle drag. */
import type { QuadOverride } from '../../state/editorTypes';
import { applyEntryResize, type ResizeEnv } from '../art/fit/applyEntryResize';
import { entryResizeFields, type EntryResize } from '../art/fit/entryResizeFields';

/** The typed override for `r`, drawn from `held`'s base when `held` is a typed override of the same entry still shown. */
function typedMark(held: QuadOverride | null, r: EntryResize): QuadOverride {
  const base = held?.by === 'typed' && held.drawable === r.drawable && held.entry === r.entry ? held : null;
  return {
    drawable: r.drawable, entry: r.entry, size0: base?.size0 ?? [r.size0[0], r.size0[1]], pivot0: base?.pivot0 ?? [r.pivot0[0], r.pivot0[1]],
    size: [r.size[0], r.size[1]], pivot: [r.pivot[0], r.pivot[1]], by: 'typed', until: null,
  };
}

/**
 * Nothing changed: false, nothing sent. Otherwise `quadOverride` becomes a `by: 'typed'` override (the viewport draws
 * the new quad at once; a still-held typed override of the same entry keeps its drawn base), then `applyEntryResize`
 * sends the one batch (fit prompt and refit when the size changed on an entry with images). Landed: the override
 * waits for the preview (`until` = the revision now); refused or cancelled: it is dropped. Resolves with whether it landed.
 */
export async function commitRig(env: ResizeEnv, r: EntryResize): Promise<boolean> {
  const fields = entryResizeFields(r);
  if (fields === null) return false;
  const mark = typedMark(env.store.getState().quadOverride, r);
  env.store.setState({ quadOverride: mark });
  const ok = await applyEntryResize(env, r, fields);
  const now = env.store.getState();
  if (now.quadOverride === mark) env.store.setState({ quadOverride: ok ? { ...mark, until: now.puppet.revision } : null });
  return ok;
}
